import React, { useEffect, useState, useCallback } from 'react';
import Select from 'react-select';
import axios from 'axios';
import './App.css';
import de from './locales/de';
import en from './locales/en';

// Globaler Sprachzustand — wird beim App-Start aus localStorage geladen.
const getLocale = () => (localStorage.getItem('competa_lang') === 'en' ? en : de);

// Auth-Token sofort beim Modulstart setzen — vor dem ersten React-Render.
// Verhindert Race-Condition bei Ctrl+F5: Dashboard feuert API-Calls bevor
// der App-useEffect den Token setzen konnte → 401 → ungewollter Logout.
const _initToken = localStorage.getItem('fiap_token');
if (_initToken) setAuthToken(_initToken);

// Übersetzt bekannte Backend-Fehlermeldungen anhand der gewählten Sprache.
function translateError(msg, lang) {
  if (!msg || !lang) return msg;
  const map = {
    'Ungültige Anmeldedaten':                               lang.errInvalidCredentials,
    'Dieses Konto ist deaktiviert':                         lang.errDeactivated,
    'Dieses Konto ist abgelaufen. Bitte einen Administrator kontaktieren.': lang.errExpired,
    'Aktuelles Passwort ist falsch':                        lang.errPasswordWrong,
    'Neues Passwort muss mindestens 6 Zeichen haben':       lang.errPasswordTooShort,
    'Benutzername bereits vergeben':                        lang.errUsernameTaken,
    'Ungültige Rolle':                                      lang.errInvalidRole,
  };
  return map[msg] || msg;
}

//--------------------------------------
//  Axios Auth-Header global setzen
//--------------------------------------

// Setzt oder entfernt den Authorization-Header für alle zukünftigen Axios-Anfragen.
// Wird beim Login, beim App-Start (Token aus localStorage) und beim Logout aufgerufen.
function setAuthToken(token) {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
}

//--------------------------------------
//  LoginPage Komponente
//--------------------------------------

// Berechnet verbleibende Tage bis zum Ablaufdatum (negativ = abgelaufen).
function daysUntilExpiry(expiresAt) {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

// Zeigt eine Ablauf-Warnung wenn das Abo in ≤10 Tagen endet.
function ExpiryWarningModal({ expiresAt, onClose, lang }) {
  const days = daysUntilExpiry(expiresAt);
  const s = days !== 1 ? (lang === null || lang?.expiryWarningTitle?.includes('Tag') ? 'en' : 'e') : '';
  const dateStr = new Date(expiresAt).toLocaleDateString('de-DE');
  const L = lang || {};
  const title = (L.expiryWarningTitle || 'Ihr Abo läuft in {days} Tag{s} ab.')
    .replace('{days}', days).replace('{s}', days !== 1 ? (L.expiryWarningTitle?.includes('day') ? 's' : 'en') : '');
  const body = (L.expiryWarningBody || 'Bitte denken Sie daran, Ihr Abo zu verlängern, um Competa nach dem {date} weiter nutzen zu können.')
    .replace('{date}', dateStr);
  return (
    <div className="dialog-overlay">
      <div className="dialog-box dialog-box--hint" onClick={e => e.stopPropagation()}>
        <div className="hint-icon">⏰</div>
        <p className="hint-text" style={{textAlign:'left'}}>
          <strong>{title}</strong><br /><br />
          {body}<br /><br />
          {L.expiryWarningContact || 'Wenden Sie sich zum Verlängern Ihres Abos an'}{' '}
          <a href="mailto:abo@miarto.de" className="link-blue">abo@miarto.de</a>
        </p>
        <button className="btn-primary" onClick={onClose}>{L.expiryWarningBtn || 'Verstanden'}</button>
      </div>
    </div>
  );
}

function LoginPage({ onLogin, lang }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  // Sendet Benutzername und Passwort an /api/login.
  // Bei Erfolg wird das JWT-Token und das User-Objekt an die übergeordnete App übergeben.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post('/api/login', { username, password });
      onLogin(res.data.token, res.data.user, res.data.sessionId);
    } catch (err) {
      setError(translateError(err.response?.data?.error, lang) || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-overlay">
      <div className="login-box">
        <img src="/logo_login.png" alt="Competa" className="login-logo" />
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-field">
            <label>Benutzername</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              required
            />
          </div>
          <div className="form-field">
            <label>Passwort</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {error && <p className="login-error">{error}</p>}
          <button type="submit" className="btn-primary login-btn" disabled={loading}>
            {loading ? 'Anmelden...' : 'Anmelden'}
          </button>
        </form>
        <p className="login-footer">© 2026 powered by <a href="https://miarto-solutions.de" target="_blank" rel="noreferrer" className="footer-link">miarto-solutions</a></p>
      </div>
    </div>
  );
}

//--------------------------------------
//  Konstanten
//--------------------------------------

const LEVELS = [
  { value: 'NFIAP', label: 'NFIAP' },
  { value: 'AFIAP', label: 'AFIAP' },
  { value: 'EFIAP', label: 'EFIAP' },
  { value: 'EFIAP_b', label: 'EFIAP/b' },
  { value: 'EFIAP_s', label: 'EFIAP/s' },
  { value: 'EFIAP_g', label: 'EFIAP/g' },
  { value: 'EFIAP_p', label: 'EFIAP/p' },
  { value: 'EFIAP_d1', label: 'EFIAP/d1' },
];

const RESULT_TYPES = [
  { value: 'acceptance', label: 'Acceptance' },
  { value: 'hm', label: 'Honorable Mentions (HM)' },
  { value: 'medal', label: 'Medaille' },
  { value: 'bluebadge', label: 'Blue Badge' },
];

const COMPETITION_TYPES = [
  { value: 'digital', label: 'Digital' },
  { value: 'print', label: 'Print' },
];

//--------------------------------------
//  Hilfsfunktionen
//--------------------------------------

// Gibt eine Ampelfarbe (rot/orange/grün) für einen Potenzialwert zurück.
const getColor = (potential) => {
  if (potential <= 1) return '#d32f2f';
  if (potential <= 3) return '#f57c00';
  return '#2e7d32';
};

// Gibt eine Ampelfarbe für einen Fortschrittswert (0–1) zurück.
// Rot < 25%, Orange < 50%, Gelb < 95%, Grün = 95%+.
const getProgressColor = (value) => {
  const percent = value * 100;
  if (percent <= 25) return '#d32f2f';
  if (percent <= 50) return '#f57c00';
  if (percent <= 95) return '#fbc02d';
  return '#2e7d32';
};

//--------------------------------------
//  API Wrapper
//--------------------------------------

// Zentrales API-Objekt: bündelt alle Backend-Aufrufe an einem Ort.
// Der Auth-Header wird automatisch durch setAuthToken global gesetzt.
const api = {
  getDashboard: (level) => axios.get(`/api/dashboard?level=${level}`),
  getCategoryStats: () => axios.get('/api/category-stats'),
  getImages: () => axios.get('/api/images'),
  getAllImages: () => axios.get('/api/all-images'),
  getCountries: () => axios.get('/api/countries'),
  getOverlimitImages: () => axios.get('/api/overlimit-images'),
  getPotentialImages: () => axios.get('/api/potential-images'),
  getAcceptancesByCountry: () => axios.get('/api/acceptances-by-country'),
  getFiapCountries: () => axios.get('/api/fiap-countries'),
  getCountryGaps: () => axios.get('/api/country-gaps'),
  getTopFlops: () => axios.get('/api/top-flops'),
  getExport: () => axios.get('/api/export'),
  postResult: (data) => axios.post('/api/result', data),
  uploadExcel: (formData, replace) =>
    axios.post(`/api/upload-excel?replace=${replace}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  // Salon-Liste
  getSalons: (q) => axios.get(`/api/salons?q=${encodeURIComponent(q)}`),
  importSalons: (formData) =>
    axios.post('/api/import-salons', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  bulkImport: (formData, replace) =>
    axios.post(`/api/bulk-import?replace=${replace}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  // DVF
  dvfDashboard:         ()    => axios.get('/api/dvf/dashboard'),
  dvfImages:            ()    => axios.get('/api/dvf/images'),
  dvfResults:           ()    => axios.get('/api/dvf/results'),
  dvfSaveResult:        (d)   => axios.post('/api/dvf/result', d),
  dvfResubmitCheck:     (id)  => axios.get(`/api/dvf/resubmit-check/${id}`),
  getImagesManage:      ()    => axios.get('/api/images/manage'),
  uploadThumbnail:      (id, formData) => axios.post(`/api/images/${id}/thumbnail`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  searchResults:        (p)   => axios.get('/api/results/search', { params: p }),
  deleteResult:         (id)  => axios.delete(`/api/results/${id}`),
  distinctTitles:       ()    => axios.get('/api/results/distinct-titles'),
  distinctCompetitions: ()    => axios.get('/api/results/distinct-competitions'),
  logout:          (sessionId) => axios.post('/api/logout', { sessionId }),
  getSessions:     ()         => axios.get('/api/sessions'),
  getUsers:        ()         => axios.get('/api/users'),
  updateUser:      (id, data) => axios.put(`/api/users/${id}`, data),
  toggleActive:    (id)       => axios.patch(`/api/users/${id}/active`),
  deleteUser:      (id)       => axios.delete(`/api/users/${id}`),
  getSuggestedLevel: ()       => axios.get('/api/suggested-level'),
};

//--------------------------------------
//  App Komponente
//--------------------------------------


//--------------------------------------
//  CreateUserModal Komponente
//--------------------------------------

// Modal für Passwort-Selbstverwaltung – für alle eingeloggten User.
function ChangePasswordModal({ onClose }) {
  const [form, setForm]     = useState({ current: '', next: '', confirm: '' });
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (form.next !== form.confirm) {
      return setError('Neues Passwort und Bestätigung stimmen nicht überein');
    }

    setLoading(true);
    try {
      await axios.put('/api/me/password', {
        currentPassword: form.current,
        newPassword:     form.next,
      });
      setSuccess('Passwort erfolgreich geändert.');
      setForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Ändern');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-box" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2 className="dialog-title">Passwort ändern</h2>
          <button className="dialog-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="dialog-form">
          <div className="form-field">
            <label>Aktuelles Passwort</label>
            <input type="password" value={form.current}
              onChange={e => setForm(f => ({ ...f, current: e.target.value }))}
              required autoFocus />
          </div>
          <div className="form-field">
            <label>Neues Passwort</label>
            <input type="password" value={form.next}
              onChange={e => setForm(f => ({ ...f, next: e.target.value }))}
              required />
          </div>
          <div className="form-field">
            <label>Neues Passwort bestätigen</label>
            <input type="password" value={form.confirm}
              onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
              required />
          </div>
          {error   && <p className="dialog-error">{error}</p>}
          {success && <p className="dialog-success">{success}</p>}
          <div className="dialog-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Speichert...' : 'Passwort ändern'}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Abbrechen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Modal zur Bildverwaltung: Thumbnails hochladen und Bildern zuordnen.
function ImageManagementModal({ onClose, onDataChanged, lang }) {
  const [images,    setImages]    = useState([]);
  const [uploading, setUploading] = useState(null); // image id currently uploading
  const [messages,  setMessages]  = useState({});   // { [id]: { ok, text } }

  useEffect(() => {
    api.getImagesManage().then(res => setImages(res.data)).catch(() => {});
  }, []);

  const handleUpload = async (imageId, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setMessages(m => ({ ...m, [imageId]: { ok: false, text: 'Datei zu groß (max. 2 MB)' } }));
      return;
    }
    setUploading(imageId);
    setMessages(m => ({ ...m, [imageId]: null }));
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.uploadThumbnail(imageId, fd);
      setImages(imgs => imgs.map(i =>
        i.id === imageId ? { ...i, thumbnail_path: res.data.thumbnail_path + '?t=' + Date.now() } : i
      ));
      setMessages(m => ({ ...m, [imageId]: { ok: true, text: '✅ Hochgeladen' } }));
      if (onDataChanged) onDataChanged();
    } catch (err) {
      setMessages(m => ({ ...m, [imageId]: { ok: false, text: err.response?.data?.error || 'Fehler beim Upload' } }));
    } finally {
      setUploading(null);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-box dialog-box--wide dialog-box--maintenance" onClick={e => e.stopPropagation()}>
        <div className="dialog-header">
          <h2 className="dialog-title">🖼️ {(lang&&lang.imageManagement)||'Bildverwaltung'}</h2>
          <button className="dialog-close" onClick={onClose}>×</button>
        </div>
        <p className="image-mgmt-hint">
          {(lang&&lang.imageManagementHint)||'Lade für jedes Bild ein Thumbnail hoch'}
        </p>
        <div className="image-mgmt-list">
          {images.map(img => (
            <div key={img.id} className="image-mgmt-row">
              {/* Thumbnail-Vorschau */}
              <div className="image-mgmt-thumb">
                {img.thumbnail_path
                  ? <img src={`/images/${img.thumbnail_path}`} alt={img.title} />
                  : <span className="image-mgmt-placeholder">{(lang&&lang.noImage)||'kein Bild'}</span>
                }
              </div>
              {/* Titel + Kategorie */}
              <div className="image-mgmt-info">
                <span className="image-mgmt-title">{img.title}</span>
                {img.category && <span className="image-mgmt-cat">{img.category}</span>}
              </div>
              {/* Upload */}
              <div className="image-mgmt-upload">
                <label className={`file-upload-btn file-upload-btn--sm${uploading === img.id ? ' file-upload-btn--disabled' : ''}`}>
                  <input type="file" accept=".jpg,.jpeg,.png,.webp"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) handleUpload(img.id, f);
                      e.target.value = '';
                    }}
                  />
                  <span>{uploading === img.id ? '⏳' : '📤 ' + ((lang&&lang.upload)||'Hochladen')}</span>
                </label>
                {messages[img.id] && (
                  <p className={messages[img.id].ok ? 'dialog-success' : 'dialog-error'} style={{margin:'4px 0 0'}}>
                    {messages[img.id].text}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Dialog zur eigenverantwortlichen Datenpflege – für alle User.
// Ermöglicht die gezielte Suche und das Löschen einzelner Ergebnisse.
function DataMaintenanceModal({ onClose, onDataChanged, lang }) {
  const [titleOptions, setTitleOptions]           = useState([]);
  const [competitionOptions, setCompetitionOptions] = useState([]);
  const [selectedTitle, setSelectedTitle]         = useState(null);
  const [selectedComp,  setSelectedComp]          = useState(null);
  const [results,   setResults]   = useState([]);
  const [searched,  setSearched]  = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting,  setDeleting]  = useState(false);

  // Dropdowns beim Öffnen befüllen
  useEffect(() => {
    api.distinctTitles().then(res =>
      setTitleOptions(res.data.map(t => ({ value: t, label: t })))
    ).catch(() => {});

    api.distinctCompetitions().then(res =>
      setCompetitionOptions(res.data.map(c => ({
        value: c.name,
        label: `${c.name} (${c.fiap_number})`,
      })))
    ).catch(() => {});
  }, []);

  const clearSearch = () => {
    setSelectedTitle(null);
    setSelectedComp(null);
    setResults([]);
    setSearched(false);
    setConfirmId(null);
  };

  const search = async () => {
    if (!selectedTitle && !selectedComp) return;
    setLoading(true);
    setSearched(true);
    setConfirmId(null);
    try {
      const res = await api.searchResults({
        title:       selectedTitle?.value || undefined,
        competition: selectedComp?.value  || undefined,
      });
      setResults(res.data);
    } catch (err) {
      alert('Suche fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      await api.deleteResult(id);
      setResults(r => r.filter(x => x.id !== id));
      setConfirmId(null);
      if (onDataChanged) onDataChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Löschen fehlgeschlagen');
    } finally {
      setDeleting(false);
    }
  };

  const awardLabel = (award) => {
    if (!award) return 'Acceptance';
    if (award === 'hm') return 'HM';
    return award;
  };

  const selectStyles = {
    container: base => ({ ...base, width: '100%' }),
    option:    base => ({ ...base, fontSize: '13px' }),
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-box dialog-box--wide dialog-box--maintenance" onClick={e => e.stopPropagation()}>
        <div className="dialog-header">
          <h2 className="dialog-title">🔧 {(lang&&lang.dataMaintenance)||'Datenpflege'}</h2>
          <button className="dialog-close" onClick={onClose}>×</button>
        </div>

        {/* Suche */}
        <div className="maintenance-search">
          <div className="form-field">
            <label>{(lang&&lang.imageTitle)||'Bildtitel'}</label>
            <Select
              options={titleOptions}
              value={selectedTitle}
              onChange={setSelectedTitle}
              placeholder={(lang&&lang.imageTitle ? lang.imageTitle + "..." : "Bild auswählen...")}
              classNamePrefix="rs"
              isClearable
              isSearchable
              styles={selectStyles}
              noOptionsMessage={() => 'Keine Bilder gefunden'}
            />
          </div>
          <div className="form-field">
            <label>{(lang&&lang.competition)||'Wettbewerb'}</label>
            <Select
              options={competitionOptions}
              value={selectedComp}
              onChange={setSelectedComp}
              placeholder={(lang&&lang.competition ? lang.competition + "..." : "Wettbewerb auswählen...")}
              classNamePrefix="rs"
              isClearable
              isSearchable
              styles={selectStyles}
              noOptionsMessage={() => (lang&&lang.noResults)||'Keine gefunden'}
            />
          </div>
          <div className="maintenance-search-actions">
            <button className="btn-primary" onClick={search}
              disabled={loading || (!selectedTitle && !selectedComp)}>
              {loading ? '⏳' : (lang&&lang.search)||'Search'}
            </button>
            {searched && (
              <button className="btn-secondary" onClick={clearSearch}>
                {(lang&&lang.clearSearch)||'Suche leeren'}
              </button>
            )}
          </div>
        </div>

        {/* Warnung */}
        {searched && (
          <div className="maintenance-warning">
            {(lang&&lang.warningDelete)||'⚠️ Das Löschen von Datensätzen erfolgt auf eigene Gefahr und ist dauerhaft.'}
          </div>
        )}

        {/* Ergebnisliste */}
        {searched && !loading && (
          results.length === 0 ? (
            <p className="no-result-hint">Keine Ergebnisse gefunden.</p>
          ) : (
            <div className="maintenance-results">
              <p className="salon-result-hint">{results.length} {(lang&&lang.resultsFound)||'Ergebnisse gefunden'}</p>
              <table className="maintenance-table">
                <thead>
                  <tr>
                    <th>{(lang&&lang.imageTitle)||'Bildtitel'}</th>
                    <th>{(lang&&lang.competition)||'Wettbewerb'}</th>
                    <th>{(lang&&lang.land)||'Land'}</th>
                    <th>{(lang&&lang.fiapNr)||'FIAP-Nr.'}</th>
                    <th>{(lang&&lang.result)||'Ergebnis'}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {results.map(r => (
                    <tr key={r.id} className={confirmId === r.id ? 'maintenance-row--confirm' : ''}>
                      <td>{r.title}</td>
                      <td>{r.salon}</td>
                      <td>{r.country}</td>
                      <td>{r.fiap_number}</td>
                      <td>{awardLabel(r.award)}</td>
                      <td className="maintenance-actions">
                        {confirmId === r.id ? (
                          <>
                            <button className="btn-danger btn-sm" onClick={() => handleDelete(r.id)} disabled={deleting}>
                              {(lang&&lang.confirmDelete)||'Ja, löschen'}
                            </button>
                            <button className="btn-secondary btn-sm" onClick={() => setConfirmId(null)}>
                              {(lang&&lang.cancel)||'Abbrechen'}
                            </button>
                          </>
                        ) : (
                          <button className="btn-danger btn-sm" onClick={() => setConfirmId(r.id)}>
                            {(lang&&lang.delete)||'Löschen'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
}

// Zeigt das Login-Protokoll aller User (nur für Admins).
function SessionLogModal({ onClose }) {
  const [sessions, setSessions] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    api.getSessions()
      .then(res => setSessions(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Timestamp kommt fertig als 'DD.MM.YYYY HH:MM' vom Server formatiert
  const formatDate = (s) => s || '—';

  const formatDuration = (secs) => {
    if (secs == null) return <span className="session-active">aktiv</span>;
    if (secs < 60)   return `${secs}s`;
    if (secs < 3600) return `${Math.floor(secs / 60)}min`;
    return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}min`;
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-box dialog-box--wide dialog-box--maintenance" onClick={e => e.stopPropagation()}>
        <div className="dialog-header">
          <h2 className="dialog-title">📋 Login-Protokoll</h2>
          <button className="dialog-close" onClick={onClose}>×</button>
        </div>
        {loading ? <p>Lädt...</p> : (
          <table className="maintenance-table">
            <thead>
              <tr>
                <th>Benutzer</th>
                <th>Angemeldet</th>
                <th>Abgemeldet</th>
                <th>Dauer</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map(s => (
                <tr key={s.id}>
                  <td><strong>{s.username}</strong></td>
                  <td>{formatDate(s.login_at)}</td>
                  <td>{formatDate(s.logout_at)}</td>
                  <td>{formatDuration(s.duration_seconds)}</td>

                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Benutzerverwaltungs-Modal für Admins: zeigt alle User, erlaubt Inline-Bearbeitung
// (Benutzername, Passwort, Rolle) und das Anlegen neuer Benutzer.
function UserManagementModal({ onClose, currentUser, lang }) {
  const [users, setUsers]         = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm]   = useState({});
  const [editMsg, setEditMsg]     = useState({});

  const [newForm, setNewForm]     = useState({ username: '', password: '', role: 'user' });
  const [newError, setNewError]   = useState('');
  const [newSuccess, setNewSuccess] = useState('');
  const [saving, setSaving]       = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Lädt die Benutzerliste beim Öffnen des Modals.
  useEffect(() => {
    api.getUsers().then(res => setUsers(res.data)).catch(() => {});
  }, []);

  // Öffnet den Inline-Editor für einen User und befüllt ihn mit aktuellen Werten.
  const startEdit = (user) => {
    setEditingId(user.id);
    setEditForm({ username: user.username, password: '', role: user.role });
    setEditMsg({});
  };

  // Speichert die Änderungen an einem bestehenden User.
  const saveEdit = async (id) => {
    setSaving(true);
    setEditMsg({});
    try {
      await api.updateUser(id, editForm);
      setUsers(u => u.map(x => x.id === id ? { ...x, username: editForm.username, role: editForm.role } : x));
      setEditingId(null);
      setEditMsg({ [id]: { ok: true, text: 'Gespeichert.' } });
    } catch (err) {
      setEditMsg({ [id]: { ok: false, text: err.response?.data?.error || 'Fehler beim Speichern' } });
    } finally {
      setSaving(false);
    }
  };

  // Schaltet den active-Status eines Users um (deaktivieren / aktivieren).
  const handleToggleActive = async (id) => {
    try {
      const res = await api.toggleActive(id);
      setUsers(u => u.map(x => x.id === id ? { ...x, active: res.data.user.active } : x));
    } catch (err) {
      alert(err.response?.data?.error || 'Fehler beim Ändern des Status');
    }
  };

  // Löscht einen User und alle seine Daten nach Bestätigung.
  const handleDelete = async (id) => {
    setSaving(true);
    try {
      await api.deleteUser(id);
      setUsers(u => u.filter(x => x.id !== id));
      setConfirmDeleteId(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Fehler beim Löschen');
    } finally {
      setSaving(false);
    }
  };

  // Legt einen neuen Benutzer an und fügt ihn der Liste hinzu.
  const handleCreate = async (e) => {
    e.preventDefault();
    setNewError('');
    setNewSuccess('');
    setSaving(true);
    try {
      const res = await axios.post('/api/users', newForm);
      setUsers(u => [...u, res.data.user]);
      setNewSuccess(`Benutzer "${newForm.username}" wurde angelegt.`);
      setNewForm({ username: '', password: '', role: 'user' });
    } catch (err) {
      setNewError(err.response?.data?.error || 'Fehler beim Anlegen');
    } finally {
      setSaving(false);
    }
  };

  const roleLabel = (r) => r === 'admin' ? '👑 Admin' : '👤 User';

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-box dialog-box--wide" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2 className="dialog-title">Benutzerverwaltung</h2>
          <button className="dialog-close" onClick={onClose}>×</button>
        </div>

        {/* Bestehende User */}
        <div className="user-list">
          {users.map(user => (
            <div key={user.id} className="user-list-item">
              {editingId === user.id ? (
                <div className="user-edit-form">
                  <input
                    type="text"
                    value={editForm.username}
                    onChange={e => setEditForm(f => ({ ...f, username: e.target.value }))}
                    placeholder="Benutzername"
                    autoFocus
                  />
                  <input
                    type="password"
                    value={editForm.password}
                    onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))}
                    placeholder={(lang&&lang.newPasswordHint)||"Neues Passwort (leer = unverändert)"}
                  />
                  <select
                    value={editForm.role}
                    onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                  <div className="user-edit-actions">
                    <button className="btn-primary btn-sm" onClick={() => saveEdit(user.id)} disabled={saving}>
                      Speichern
                    </button>
                    <button className="btn-secondary btn-sm" onClick={() => setEditingId(null)}>
                      Abbrechen
                    </button>
                  </div>
                  {editMsg[user.id] && (
                    <p className={editMsg[user.id].ok ? 'dialog-success' : 'dialog-error'}>
                      {editMsg[user.id].text}
                    </p>
                  )}
                </div>
              ) : confirmDeleteId === user.id ? (
                <div className="user-delete-confirm">
                  <span className="user-delete-warning">
                    Alle Daten von <strong>{user.username}</strong> unwiderruflich löschen?
                  </span>
                  <div className="user-edit-actions">
                    <button className="btn-danger btn-sm" onClick={() => handleDelete(user.id)} disabled={saving}>
                      Ja, löschen
                    </button>
                    <button className="btn-secondary btn-sm" onClick={() => setConfirmDeleteId(null)}>
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="user-info">
                    <span className={`user-name${!user.active ? ' user-name--inactive' : ''}`}>
                      {user.username}
                    </span>
                    <span className="user-role-badge">{roleLabel(user.role)}</span>
                    {!user.active && <span className="user-inactive-badge">{(lang&&lang.deactivated)||'deaktiviert'}</span>}
                  </div>
                  <div className="user-edit-actions">
                    <button className="btn-secondary btn-sm" onClick={() => startEdit(user)}>
                      Bearbeiten
                    </button>
                    {user.id !== currentUser?.id && (
                      <>
                        <button
                          className={`btn-sm ${user.active ? 'btn-warning' : 'btn-success'}`}
                          onClick={() => handleToggleActive(user.id)}
                        >
                          {user.active ? (lang&&lang.deactivate)||'Deaktivieren' : (lang&&lang.activate)||'Aktivieren'}
                        </button>
                        <button className="btn-danger btn-sm" onClick={() => setConfirmDeleteId(user.id)}>
                          Löschen
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        <hr className="dialog-divider" />

        {/* Neuen User anlegen */}
        <p className="dialog-section-title">Neuen Benutzer anlegen</p>
        <form onSubmit={handleCreate} className="dialog-form">
          <div className="form-field">
            <label>Benutzername</label>
            <input
              type="text"
              value={newForm.username}
              onChange={e => setNewForm(f => ({ ...f, username: e.target.value }))}
              required
            />
          </div>
          <div className="form-field">
            <label>Passwort</label>
            <input
              type="password"
              value={newForm.password}
              onChange={e => setNewForm(f => ({ ...f, password: e.target.value }))}
              required
            />
          </div>
          <div className="form-field">
            <label>Rolle</label>
            <select
              value={newForm.role}
              onChange={e => setNewForm(f => ({ ...f, role: e.target.value }))}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          {newError   && <p className="dialog-error">{newError}</p>}
          {newSuccess && <p className="dialog-success">{newSuccess}</p>}
          <div className="dialog-actions">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? (lang&&lang.saving)||'Speichert...' : (lang&&lang.create)||'Anlegen'}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Schließen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

//--------------------------------------
//  SalonSearchCard Komponente
//--------------------------------------

// Karte zum Durchsuchen der importierten FIAP-Salon-Liste.
// Unterstützt Suche nach Name, FIAP-Nummer und Closing-Datumsbereich.
// Ergebnisse können per Klick aufgeklappt werden um alle Salon-Details anzuzeigen.
function SalonSearchCard({ lang }) {
  const [query, setQuery]           = useState({ name: '', fiap: '', closingFrom: '', closingTo: '' });
  const [results, setResults]       = useState([]);
  const [searched, setSearched]     = useState(false);
  const [loading, setLoading]       = useState(false);
  const [expanded, setExpanded]     = useState(null);

  // Baut die Query-Parameter aus dem Formular zusammen und ruft /api/salons/search auf.
  const search = async () => {
    setLoading(true);
    setSearched(true);
    setExpanded(null);
    try {
      const params = new URLSearchParams();
      if (query.name)    params.append('name', query.name.trim());
      if (query.fiap)    params.append('fiap', query.fiap.trim());
      // Closing: Monat und/oder Jahr als Filter
      if (query.closingFrom) params.append('closingFrom', query.closingFrom);
      if (query.closingTo)   params.append('closingTo',   query.closingTo);
      const res = await axios.get(`/api/salons/search?${params}`);
      setResults(res.data);
    } catch (err) {
      console.error('Salon Suche Fehler:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Löst die Suche per Enter-Taste aus.
  const handleKey = (e) => {
    if (e.key === 'Enter') search();
  };

  // Setzt alle Suchfelder und Ergebnisse zurück.
  const reset = () => {
    setQuery({ name: '', fiap: '', closingFrom: '', closingTo: '' });
    setResults([]);
    setSearched(false);
    setExpanded(null);
  };

  return (
    <div className="card import-card mb-24">
      <h2>{lang.searchCompetitions}</h2>

      {/* Suchfelder */}
      <div className="salon-search-grid">

        <div className="form-field form-field--no-margin">
          <label>{lang.salonName}</label>
          <input
            type="text"
            value={query.name}
            onChange={(e) => setQuery(q => ({ ...q, name: e.target.value }))}
            onKeyDown={handleKey}
            placeholder="z. B. Bristol, BOKA BAY..."
          />
        </div>

        <div className="form-field form-field--no-margin">
          <label>{lang.fiapNumber}</label>
          <input
            type="text"
            value={query.fiap}
            onChange={(e) => setQuery(q => ({ ...q, fiap: e.target.value }))}
            onKeyDown={handleKey}
            placeholder="z. B. 2026/013"
          />
        </div>

        <div className="form-field form-field--no-margin">
          <label>{lang.closingFromTo}</label>
          <div className="date-range">
            <input
              type="date"
              value={query.closingFrom}
              onChange={(e) => setQuery(q => ({ ...q, closingFrom: e.target.value }))}
              onKeyDown={handleKey}
            />
            <span className="date-separator">–</span>
            <input
              type="date"
              value={query.closingTo}
              onChange={(e) => setQuery(q => ({ ...q, closingTo: e.target.value }))}
              onKeyDown={handleKey}
            />
          </div>
        </div>

        <div className="btn-group">
          <label className="btn-group-spacer">&nbsp;</label>
          <button
            onClick={search}
            disabled={loading}
            className="btn-primary"
          >
            {loading ? '⏳' : (lang&&lang.search)||'Search'}
          </button>
        </div>
      </div>
      {searched && (
        <div style={{textAlign: 'right', marginTop: '-4px'}}>
          <button onClick={reset} className="search-clear-btn">
            {(lang&&lang.clearSearch)||'Suche leeren'}
          </button>
        </div>
      )}

      {/* Ergebnisse */}
      {searched && !loading && (
        results.length === 0 ? (
          <p className="no-result-hint">Keine Wettbewerbe gefunden.</p>
        ) : (
          <div>
            <p className="salon-result-hint">
              {results.length} Ergebnis{results.length !== 1 ? 'se' : ''}
            </p>
            <div className="salon-result-list">
              {results.map((s, i) => (
                <div key={i} className={`salon-result-item${expanded === i ? ' salon-result-item--expanded' : ''}`}>
                  {/* Zeile (immer sichtbar) */}
                  <div
                    onClick={() => setExpanded(expanded === i ? null : i)}
                    className="salon-result-header"
                  >
                    <span className="salon-fiap-number">{s.fiap_number}</span>
                    <span className="salon-name">{s.name}</span>
                    <span className="salon-closing">{s.closing ? `📅 ${s.closing}` : '—'}</span>
                    <span className="salon-expand-toggle">{expanded === i ? '▲ ' + lang.less : '▼ ' + lang.details}</span>
                  </div>

                  {/* Detailbereich (aufklappbar) */}
                  {expanded === i && (
                    <div className="salon-detail">

                      {/* Daten-Zeile: Closing / Judging / Notification */}
                      {(s.closing || s.judging || s.notification) && (
                        <div className="salon-dates">
                          {s.closing      && <span>📅 <b>Closing:</b> {s.closing}</span>}
                          {s.judging      && <span>⚖️ <b>Judging:</b> {s.judging}</span>}
                          {s.notification && <span>📬 <b>Notification:</b> {s.notification}</span>}
                        </div>
                      )}

                      {s.type     && <div><span className="label-muted">Typ: </span>{s.type}</div>}
                      {s.sections && <div><span className="label-muted">Sektionen: </span>{s.sections}</div>}
                      {s.price && (
                        <div className="span-2">
                          <span className="label-muted">Preis:</span>
                          <div className="detail-indent">
                            {s.price.split('\n').filter(Boolean).map((line, i) => (
                              <div key={i}>{line}</div>
                            ))}
                          </div>
                        </div>
                      )}
                      {s.type_of_sections && (
                        <div className="span-2">
                          <span className="label-muted">Kategorien:</span>
                          <div className="detail-indent">
                            {s.type_of_sections.split('\n').filter(Boolean).map((line, i) => (
                              <div key={i}>{line}</div>
                            ))}
                          </div>
                        </div>
                      )}
                      {s.info && (
                        <div className="span-2">
                          <span className="label-muted">Info: </span>{s.info}
                        </div>
                      )}
                      {s.email && (
                        <div>
                          <span className="label-muted">E-Mail: </span>
                          <a href={`mailto:${s.email}`} className="link-blue">{s.email}</a>
                        </div>
                      )}
                      {s.homepage && (
                        <div>
                          <span className="label-muted">Website: </span>
                          <a href={s.homepage} target="_blank" rel="noreferrer" className="link-blue">{s.homepage}</a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      )}
    </div>
  );
}


// ============================================================
// 🏅 DVF-Dashboard
// ============================================================
function DVFDashboard({ lang }) {
  const [stats,   setStats]   = useState(null);
  const [images,  setImages]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.dvfDashboard(), api.dvfImages()])
      .then(([s, i]) => { setStats(s.data); setImages(i.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p style={{padding:'20px'}}>Lade DVF-Daten...</p>;
  if (!stats)  return <p style={{padding:'20px'}}>Keine DVF-Daten vorhanden.</p>;

  const retinaNext = stats.retina < 10 ? {label:'Bronzenadel', need: 10 - stats.retina}
    : stats.retina < 20 ? {label:'Silbernadel', need: 20 - stats.retina}
    : stats.retina < 30 ? {label:'Goldnadel',   need: 30 - stats.retina}
    : stats.retina < 40 ? {label:'Künstler',     need: 40 - stats.retina}
    : null;

  return (
    <div>
      <div className="grid">
        {/* Übersicht */}
        <div className="card">
          <h2>🏅 DVF Übersicht</h2>
          <ul className="flop-list">
            {[
              {label: 'Einreichungen', val: stats.total},
              {label: 'Annahmen',      val: stats.acceptances},
              {label: 'Urkunden',      val: stats.urkunden},
              {label: 'Medaillen',     val: stats.medaillen},
            ].map((r,i) => (
              <li key={i} className="flop-item">
                <div className="flop-title">{r.label}</div>
                <span className="badge gray">{r.val}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Digital / Print */}
        <div className="card">
          <h2>📷 Digital / Print</h2>
          <ul className="flop-list">
            <li className="flop-item"><div className="flop-title">Digital</div><span className="badge neutral">{stats.digital}</span></li>
            <li className="flop-item"><div className="flop-title">Print</div><span className="badge neutral">{stats.print}</span></li>
          </ul>
        </div>

        {/* RETINA / IRIS */}
        <div className="card">
          <h2>⭐ RETINA / IRIS</h2>
          <ul className="flop-list">
            <li className="flop-item">
              <div className="flop-left">
                <div className="flop-title">RETINA-Punkte</div>
                {stats.retinaTitle && <div className="flop-category">{stats.retinaTitle}</div>}
                {retinaNext && <div className="flop-category">→ {retinaNext.need} P bis {retinaNext.label}</div>}
              </div>
              <span className="badge warning">{stats.retina}</span>
            </li>
            <li className="flop-item">
              <div className="flop-title">IRIS-Punkte</div>
              <span className="badge neutral">{stats.iris}</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Top Bilder */}
      {images.length > 0 && (
        <div className="grid">
          <div className="card">
            <h2>⭐ Top Bilder</h2>
            <ul className="flop-list">
              {images.map((img, i) => (
                <li key={i} className="flop-item">
                  <div className="flop-left">
                    <div className="flop-title">{img.title}</div>
                    <div className="flop-category">{img.category}</div>
                  </div>
                  <div style={{display:'flex',gap:'4px',flexShrink:0}}>
                    {img.medaillen > 0 && <span className="badge danger">🥇 {img.medaillen}</span>}
                    {img.urkunden  > 0 && <span className="badge warning">📜 {img.urkunden}</span>}
                    {img.acceptances > 0 && <span className="badge neutral">✓ {img.acceptances}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// 📋 DVF-Daten-Tab (Ergebnis erfassen)
// ============================================================
const DVF_LEVELS = [
  {value:'orts',      label:'Ortsebene'},
  {value:'bezirk',   label:'Bezirksebene'},
  {value:'landes',   label:'Landesebene'},
  {value:'regional', label:'Regionalebene (Nord/Süd)'},
  {value:'bundes',   label:'Bundesebene (DFM)'},
  {value:'sonder',   label:'Sonderwettbewerb'},
];
const DVF_VERBAENDE = [
  'DVF Bundesverband','DVF Nordmark','DVF Bayern','DVF Baden-Württemberg',
  'DVF Saarland','DVF Hessen/Rheinland-Pfalz','DVF Sachsen/SA/TH',
  'DVF Berlin/BB/MV','DVF Rheinland','DVF Westfalen','DVF Hamburg',
];

function DVFDataTab({ lang, refreshKey, setRefreshKey }) {
  const [allImages, setAllImages]   = useState([]);
  const [form, setForm]             = useState({ title:'', newTitle:'', salon:'', level:'landes', verband:'DVF Nordmark', type:'digital', award:'' });
  const [warning, setWarning]       = useState('');
  const [saving, setSaving]         = useState(false);

  useEffect(() => {
    api.getAllImages().then(r => setAllImages(r.data)).catch(() => {});
  }, [refreshKey]);

  const checkResubmit = async (imageId) => {
    if (!imageId) return;
    try {
      const r = await api.dvfResubmitCheck(imageId);
      if (r.data.hasAward) {
        const awardLabel = r.data.award === 'medaille' ? 'eine Medaille' : 'eine Urkunde';
        setWarning(`⚠️ Dieses Bild hat bereits ${awardLabel} erhalten (${DVF_LEVELS.find(l=>l.value===r.data.dvf_level)?.label || r.data.dvf_level}). Bitte beachte die DVF-Wiedereinreichungsregeln.`);
      } else { setWarning(''); }
    } catch { setWarning(''); }
  };

  const handleTitleChange = (val) => {
    setForm(f => ({...f, title: val, newTitle:''}));
    const img = allImages.find(i => i.title === val);
    if (img) checkResubmit(img.id);
    else setWarning('');
  };

  const save = async () => {
    const title = form.newTitle || form.title;
    if (!title || !form.salon || !form.level) {
      alert('Bitte Bild, Wettbewerb und Ebene angeben');
      return;
    }
    setSaving(true);
    try {
      await api.dvfSaveResult({ title, salonName: form.salon, dvfLevel: form.level, dvfVerband: form.verband, type: form.type, award: form.award || null });
      alert('Gespeichert!');
      setForm(f => ({...f, title:'', newTitle:'', salon:'', award:''}));
      setWarning('');
      setRefreshKey(k => k + 1);
    } catch (err) { alert(err.response?.data?.error || 'Fehler'); }
    finally { setSaving(false); }
  };

  return (
    <div className="card entry-card import-card mb-24">
      <h2 className="entry-card-title">🏅 DVF-Ergebnis hinzufügen</h2>
      {warning && <div className="dvf-warning">{warning}</div>}
      <div className="entry-form-grid">
        <div className="form-field">
          <label>Bild wählen:</label>
          <select value={form.title} onChange={e => handleTitleChange(e.target.value)}>
            <option value="">-- vorhandenes Bild --</option>
            {allImages.map((img,i) => <option key={i} value={img.title}>{img.title}</option>)}
          </select>
        </div>
        <div className="form-field">
          <label>Oder neues Bild:</label>
          <input type="text" value={form.newTitle} onChange={e => setForm(f=>({...f,newTitle:e.target.value,title:''}))} placeholder="Neuer Bildtitel" />
        </div>
        <div className="form-field">
          <label>Wettbewerb:</label>
          <input type="text" value={form.salon} onChange={e => setForm(f=>({...f,salon:e.target.value}))} placeholder="z. B. Landesmeisterschaft Nordmark 2026" />
        </div>
        <div className="form-field">
          <label>Verband:</label>
          <select value={form.verband} onChange={e => setForm(f=>({...f,verband:e.target.value}))}>
            {DVF_VERBAENDE.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div className="form-field">
          <label>Ebene:</label>
          <select value={form.level} onChange={e => setForm(f=>({...f,level:e.target.value}))}>
            {DVF_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </div>
        <div className="form-field">
          <label>Typ:</label>
          <div className="radio-group compact-radio-group">
            {['digital','print'].map(t => (
              <label key={t} className="radio-item">
                <input type="radio" value={t} checked={form.type===t} onChange={() => setForm(f=>({...f,type:t}))} />
                {t.charAt(0).toUpperCase()+t.slice(1)}
              </label>
            ))}
          </div>
        </div>
      </div>
      <p className="section-label">Auszeichnung:</p>
      <div className="radio-group result-radio-group">
        {[{value:'',label:'Keine Annahme'},{value:'acceptance',label:'Annahme'},{value:'urkunde',label:'Urkunde'},{value:'medaille',label:'Medaille'}].map(a => (
          <label key={a.value} className="radio-item">
            <input type="radio" value={a.value} checked={form.award===a.value} onChange={() => setForm(f=>({...f,award:a.value}))} />
            {a.label}
          </label>
        ))}
      </div>
      <div className="button-row">
        <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Speichert...' : 'Speichern'}</button>
      </div>
    </div>
  );
}

// ============================================================
// ⚙️ AdminTab — drei Bereiche inline als Karten
// ============================================================
function AdminTab({ currentUser, lang }) {

  // ── FIAP-Import ──────────────────────────────────────────
  const [salonImporting,    setSalonImporting]    = useState(false);
  const [salonImportResult, setSalonImportResult] = useState(null);
  const [salonReplaceMode,  setSalonReplaceMode]  = useState(false);

  const importSalonList = useCallback(async (file) => {
    setSalonImporting(true);
    setSalonImportResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(
        `/api/import-salons?replace=${salonReplaceMode}`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const d = res.data;
      const modeLabel = d.replaced ? 'Tabelle geleert + ' : '';
      setSalonImportResult({ success: true,
        message: `${modeLabel}${d.inserted} neu importiert, ${d.updated} aktualisiert${d.errors > 0 ? `, ${d.errors} Fehler` : ''}` });
    } catch (err) {
      setSalonImportResult({ success: false,
        message: err.response?.data?.error || err.message });
    } finally { setSalonImporting(false); }
  }, [salonReplaceMode]);

  // ── Benutzerverwaltung ───────────────────────────────────
  const [users,           setUsers]           = useState([]);
  const [editingId,       setEditingId]       = useState(null);
  const [editForm,        setEditForm]        = useState({});
  const [editMsg,         setEditMsg]         = useState({});
  const [newForm,         setNewForm]         = useState({ username: '', password: '', role: 'user' });
  const [newError,        setNewError]        = useState('');
  const [newSuccess,      setNewSuccess]      = useState('');
  const [saving,          setSaving]          = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    api.getUsers().then(res => setUsers(res.data)).catch(() => {});
  }, []);

  const startEdit = (user) => {
    setEditingId(user.id);
    setEditForm({ username: user.username, password: '', role: user.role });
    setEditMsg({});
  };

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      await api.updateUser(id, editForm);
      setUsers(u => u.map(x => x.id === id ? { ...x, username: editForm.username, role: editForm.role } : x));
      setEditingId(null);
    } catch (err) {
      setEditMsg({ [id]: { text: err.response?.data?.error || 'Fehler' } });
    } finally { setSaving(false); }
  };

  const handleToggleActive = async (id) => {
    try {
      const res = await api.toggleActive(id);
      setUsers(u => u.map(x => x.id === id ? { ...x, active: res.data.user.active } : x));
    } catch (err) { alert(err.response?.data?.error || 'Fehler'); }
  };

  const handleDelete = async (id) => {
    setSaving(true);
    try {
      await api.deleteUser(id);
      setUsers(u => u.filter(x => x.id !== id));
      setConfirmDeleteId(null);
    } catch (err) { alert(err.response?.data?.error || 'Fehler'); }
    finally { setSaving(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setNewError(''); setNewSuccess(''); setSaving(true);
    try {
      const res = await axios.post('/api/users', newForm);
      setUsers(u => [...u, res.data.user]);
      setNewSuccess(`Benutzer "${newForm.username}" angelegt.`);
      setNewForm({ username: '', password: '', role: 'user' });
    } catch (err) {
      setNewError(err.response?.data?.error || 'Fehler');
    } finally { setSaving(false); }
  };

  const roleLabel = (r) => r === 'admin' ? '👑 Admin' : '👤 User';

  // ── Login-Protokoll ──────────────────────────────────────
  const [sessions,    setSessions]    = useState([]);
  const [sessLoading, setSessLoading] = useState(true);
  const [sessFilter,  setSessFilter]  = useState('');
  const [sessLimit,   setSessLimit]   = useState(3);

  const loadSessions = useCallback(() => {
    setSessLoading(true);
    const params = new URLSearchParams();
    if (sessFilter) params.append('username', sessFilter);
    params.append('limit', sessLimit);
    axios.get(`/api/sessions?${params}`)
      .then(res => setSessions(res.data))
      .catch(() => {})
      .finally(() => setSessLoading(false));
  }, [sessFilter, sessLimit]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Timestamp kommt fertig als 'DD.MM.YYYY HH:MM' vom Server formatiert
  const formatDate = (s) => s || '-';

  const formatDuration = (secs) => {
    if (secs == null) return 'aktiv';
    if (secs < 60)   return secs + 's';
    if (secs < 3600) return Math.floor(secs / 60) + 'min';
    return Math.floor(secs / 3600) + 'h ' + Math.floor((secs % 3600) / 60) + 'min';
  };

  return (
    <div className="admin-tab">

      <div className="card admin-section">
        <h2>{(lang || {}).adminImport || 'Wettbewerbsliste importieren'}</h2>
        <p className="import-hint">{(lang||{}).importCompetitionsHint||"Offizielle Wettbewerbsliste importieren"}</p>
        <label className="checkbox-label">
          <input type="checkbox" checked={salonReplaceMode}
            onChange={e => setSalonReplaceMode(e.target.checked)} />
          {(lang||{}).clearTable||"Tabelle vorher leeren (Neuimport)"}
        </label>
        {salonImportResult && (
          <p className={`import-result-msg import-result-msg--${salonImportResult.success ? 'success' : 'error'}`}>
            {salonImportResult.message}
          </p>
        )}
        <div style={{marginTop: '16px'}}>
          <label className={`file-upload-btn${salonImporting ? ' file-upload-btn--disabled' : ''}`}>
            <input type="file" accept=".xlsx,.pdf"
              onChange={e => { const f = e.target.files?.[0]; if (f) importSalonList(f); e.target.value = ''; }} />
            <span>{salonImporting ? (lang&&lang.importing)||'⏳ Importiert...' : '📂 ' + ((lang&&lang.selectFile)||'Datei auswählen')}</span>
          </label>
        </div>
      </div>

      <div className="card admin-section">
        <h2>{(lang || {}).userManagement || 'Benutzerverwaltung'}</h2>
        <div className="user-list">
          {users.map(user => (
            <div key={user.id} className="user-list-item">
              {editingId === user.id ? (
                <div className="user-edit-form">
                  <input type="text" value={editForm.username} autoFocus
                    onChange={e => setEditForm(f => ({ ...f, username: e.target.value }))}
                    placeholder="Benutzername" />
                  <input type="password" value={editForm.password}
                    onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))}
                    placeholder={(lang&&lang.newPasswordHint)||"Neues Passwort (leer = unverändert)"} />
                  <select value={editForm.role}
                    onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                  <div className="user-edit-actions">
                    <button className="btn-primary btn-sm" onClick={() => saveEdit(user.id)} disabled={saving}>{(lang||{}).save||"Speichern"}</button>
                    <button className="btn-secondary btn-sm" onClick={() => setEditingId(null)}>{(lang||{}).cancel||"Abbrechen"}</button>
                  </div>
                  {editMsg[user.id] && <p className="dialog-error">{editMsg[user.id].text}</p>}
                </div>
              ) : confirmDeleteId === user.id ? (
                <div className="user-delete-confirm">
                  <span className="user-delete-warning">Alle Daten von <strong>{user.username}</strong> unwiderruflich löschen?</span>
                  <div className="user-edit-actions">
                    <button className="btn-danger btn-sm" onClick={() => handleDelete(user.id)} disabled={saving}>{(lang&&lang.confirmDelete)||'Ja, löschen'}</button>
                    <button className="btn-secondary btn-sm" onClick={() => setConfirmDeleteId(null)}>{(lang&&lang.cancel)||'Abbrechen'}</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="user-info">
                    <span className={`user-name${!user.active ? ' user-name--inactive' : ''}`}>{user.username}</span>
                    <span className="user-role-badge">{roleLabel(user.role)}</span>
                    {user.expires_at && (
                      <span className={`user-expires-badge${new Date(user.expires_at) < new Date() ? ' user-expires-badge--expired' : ''}`}>
                        📅 {new Date(user.expires_at).toLocaleDateString('de-DE')}
                      </span>
                    )}
                    {!user.active && <span className="user-inactive-badge">{(lang&&lang.deactivated)||'deaktiviert'}</span>}
                  </div>
                  <div className="user-edit-actions">
                    <button className="btn-secondary btn-sm" onClick={() => startEdit(user)}>{(lang&&lang.editUser)||'Bearbeiten'}</button>
                    {user.id !== currentUser?.id && (<>
                      <button className={`btn-sm ${user.active ? 'btn-warning' : 'btn-success'}`}
                        onClick={() => handleToggleActive(user.id)}>
                        {user.active ? (lang&&lang.deactivate)||'Deaktivieren' : (lang&&lang.activate)||'Aktivieren'}
                      </button>
                      <button className="btn-danger btn-sm" onClick={() => setConfirmDeleteId(user.id)}>{(lang&&lang.deleteUser)||'Löschen'}</button>
                    </>)}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
        <hr className="dialog-divider" />
        <p className="dialog-section-title">{(lang||{}).createUser||'Neuen Benutzer anlegen'}</p>
        <form onSubmit={handleCreate} className="admin-new-user-form">
          <div className="form-field">
            <label>{(lang||{}).username||"Benutzername"}</label>
            <input type="text" value={newForm.username}
              onChange={e => setNewForm(f => ({ ...f, username: e.target.value }))} required />
          </div>
          <div className="form-field">
            <label>{(lang||{}).password||"Passwort"}</label>
            <input type="password" value={newForm.password}
              onChange={e => setNewForm(f => ({ ...f, password: e.target.value }))} required />
          </div>
          <div className="form-field">
            <label>{(lang||{}).role||"Rolle"}</label>
            <select value={newForm.role} onChange={e => setNewForm(f => ({ ...f, role: e.target.value }))}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          {newError   && <p className="dialog-error">{newError}</p>}
          {newSuccess && <p className="dialog-success">{newSuccess}</p>}
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? (lang&&lang.saving)||'Speichert...' : (lang&&lang.create)||'Anlegen'}
          </button>
        </form>
      </div>

      <div className="card admin-section">
        <h2>{(lang || {}).loginLog || 'Login-Protokoll'}</h2>
        <div className="session-filter">
          <div className="form-field">
            <label>{(lang||{}).username||'Benutzer'}</label>
            <input type="text" value={sessFilter}
              onChange={e => setSessFilter(e.target.value)}
              placeholder="z. B. Arne" />
          </div>
          <div className="form-field">
            <label>{(lang||{}).sessResultsLabel||'Ergebnisse / Benutzer'}</label>
            <input type="number" value={sessLimit} min="1" max="100"
              onChange={e => setSessLimit(Math.max(1, parseInt(e.target.value)||1))} />
          </div>
        </div>
        {sessLoading ? <p>Laedt...</p> : (
          <table className="maintenance-table">
            <thead>
              <tr><th>{(lang||{}).username||"Benutzer"}</th><th>{(lang||{}).loggedIn||"Angemeldet"}</th><th>{(lang||{}).loggedOut||"Abgemeldet"}</th><th>{(lang||{}).duration||"Dauer"}</th></tr>
            </thead>
            <tbody>
              {sessions.map(s => (
                <tr key={s.id}>
                  <td><strong>{s.username}</strong></td>
                  <td>{formatDate(s.login_at)}</td>
                  <td>{formatDate(s.logout_at)}</td>
                  <td className={s.duration_seconds == null ? 'session-active' : ''}>{formatDuration(s.duration_seconds)}</td>

                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}


// Wurzel-Komponente: verwaltet ausschließlich den Auth-Zustand.
// Zeigt entweder LoginPage oder Dashboard – keine eigene UI.
function App() {
  // Initialisiert den User-State aus localStorage, damit die Session über
  // Seitenreloads hinaus erhalten bleibt.
  const [currentUser, setCurrentUser] = useState(() => {
    const saved = localStorage.getItem('fiap_user');
    return saved ? JSON.parse(saved) : null;
  });

  const [lang, setLang] = useState(getLocale);
  const [expiryWarning, setExpiryWarning] = useState(null); // expires_at wenn Warnung nötig

  const toggleLang = () => {
    const isEn = lang === en;
    const next = isEn ? de : en;
    localStorage.setItem('competa_lang', isEn ? 'de' : 'en');
    setLang(next);
  };

  // Dark Mode global — lebt in App damit er auch auf der Login-Seite aktiv bleibt.
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('competa_dark') === 'true');
  const [org, setOrg] = useState(() => localStorage.getItem('competa_org') || 'fiap');

  const toggleOrg = () => {
    const next = org === 'fiap' ? 'dvf' : 'fiap';
    localStorage.setItem('competa_org', next);
    setOrg(next);
  };

  useEffect(() => {
    document.body.classList.toggle('dark', darkMode);
    localStorage.setItem('competa_dark', darkMode);
  }, [darkMode]);

  useEffect(() => {
    const token = localStorage.getItem('fiap_token');
    if (token) setAuthToken(token);

    // 401-Interceptor: bei abgelaufenem/ungültigem Token automatisch ausloggen
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('fiap_token');
          localStorage.removeItem('fiap_user');
          setAuthToken(null);
          setCurrentUser(null);
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  // Speichert Token, sessionId und User-Daten persistent und aktiviert den Auth-Header.
  const handleLogin = (token, user, sessionId) => {
    localStorage.setItem('fiap_token', token);
    localStorage.setItem('fiap_user', JSON.stringify(user));
    if (sessionId) localStorage.setItem('fiap_session', sessionId);
    setAuthToken(token);
    setCurrentUser(user);
    // Ablauf-Warnung prüfen: anzeigen wenn ≤10 Tage bis Ablauf
    if (user.expires_at) {
      const days = daysUntilExpiry(user.expires_at);
      if (days !== null && days <= 10 && days > 0) {
        setExpiryWarning(user.expires_at);
      }
    }
  };

  // Sendet Logout an den Server (Session schließen) und löscht alle Auth-Daten.
  const handleLogout = () => {
    const sessionId = localStorage.getItem('fiap_session');
    api.logout(sessionId).catch(() => {});
    localStorage.removeItem('fiap_token');
    localStorage.removeItem('fiap_user');
    localStorage.removeItem('fiap_session');
    setAuthToken(null);
    setCurrentUser(null);
  };

  if (!currentUser) {
    return <LoginPage onLogin={handleLogin} lang={lang} />;
  }

  return (
    <>
      {expiryWarning && <ExpiryWarningModal expiresAt={expiryWarning} onClose={() => setExpiryWarning(null)} lang={lang} />}
      <Dashboard currentUser={currentUser} onLogout={handleLogout} lang={lang} toggleLang={toggleLang} darkMode={darkMode} setDarkMode={setDarkMode} org={org} toggleOrg={toggleOrg} />
    </>
  );
}

function Dashboard({ currentUser, onLogout, lang, toggleLang, darkMode, setDarkMode, org, toggleOrg }) {

  const isAdmin = currentUser.role === 'admin';

  //--------------------------------------
  //  State: Dialoge
  //--------------------------------------

  const [activeTab, setActiveTab] = useState('dashboard');
  const [refreshKey, setRefreshKey] = useState(0);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showTemplateHint, setShowTemplateHint] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);
  const [showImageMgmt,   setShowImageMgmt]   = useState(false);
  const [showSessionLog,  setShowSessionLog]  = useState(false);

  //--------------------------------------
  //  State: Dashboard
  //--------------------------------------

  const [data, setData] = useState(null);
  // null solange der Suggested-Level noch nicht geladen ist – verhindert
  // dass das Dashboard mit dem falschen Level vorab rendert.
  const [level, setLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Holt beim ersten Laden den ersten noch nicht erreichten Level und setzt ihn.
  // loadDashboard wartet auf level !== null, daher gibt es keine Race Condition.
  useEffect(() => {
    api.getSuggestedLevel()
      .then(res => setLevel(res.data.level))
      .catch(() => setLevel('EFIAP')); // Fallback bei Fehler
  }, []);

  //--------------------------------------
  //  State: Listen-Daten
  //--------------------------------------

  const [countries, setCountries] = useState([]);
  const [overlimit, setOverlimit] = useState([]);
  const [potentialImages, setPotentialImages] = useState([]);
  const [topImages, setTopImages] = useState([]);
  const [allImages, setAllImages] = useState([]);
  const [fiapCountries, setFiapCountries] = useState([]);
  const [countryStats, setCountryStats] = useState([]);
  const [countryGaps, setCountryGaps] = useState([]);
  const [categoryStats, setCategoryStats] = useState([]);
  const [topFlops, setTopFlops] = useState([]);
  const [regions, setRegions] = useState([]);
  const [bestPerRegion, setBestPerRegion] = useState([]);

  //--------------------------------------
  //  State: Formular
  //--------------------------------------

  const [formData, setFormData] = useState({
    country: '',
    resultType: 'acceptance',
    awardText: '',
    competitionType: 'digital',
    salonName: '',
    fiapNumber: '',
  });

  const [imageRows, setImageRows] = useState([{ selectedTitle: '', newTitle: '' }]);

  //--------------------------------------
  //  State: Salon-Suche
  //--------------------------------------

  const [salonOptions, setSalonOptions] = useState([]);
  const [salonInputValue, setSalonInputValue] = useState('');
  const [salonSelected, setSalonSelected] = useState(null);
  const [salonSearchLoading, setSalonSearchLoading] = useState(false);

  //--------------------------------------
  //  State: Gesamtliste-Import
  //--------------------------------------

  const [excelReplaceMode, setExcelReplaceMode] = useState(false);

  //--------------------------------------
  //  State: Salon-Import
  //--------------------------------------

  const [salonImporting, setSalonImporting] = useState(false);
  const [salonImportResult, setSalonImportResult] = useState(null);
  const [salonReplaceMode, setSalonReplaceMode] = useState(false);

  //--------------------------------------
  //  State: Bulk-Import
  //--------------------------------------

  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState(null);
  const [bulkReplaceMode, setBulkReplaceMode] = useState(false);

  //--------------------------------------
  //  State: UI
  //--------------------------------------

  const [selectedImage, setSelectedImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  //--------------------------------------
  //  Formular Helper
  //--------------------------------------

  // Aktualisiert ein einzelnes Feld im Formular-State ohne die anderen Felder zu überschreiben.
  const updateForm = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Setzt das Ergebnis-Eingabeformular vollständig zurück inkl. Salon-Auswahl.
  const resetForm = () => {
    setFormData({
      country: '',
      resultType: 'acceptance',
      awardText: '',
      competitionType: 'digital',
      salonName: '',
      fiapNumber: '',
    });
    setImageRows([{ selectedTitle: '', newTitle: '' }]);
    setSalonSelected(null);
    setSalonInputValue('');
    setSalonOptions([]);
  };

  const updateImageRow = (index, field, value) => {
    setImageRows(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const addImageRow = () => {
    setImageRows(prev => [...prev, { selectedTitle: '', newTitle: '' }]);
  };

  //--------------------------------------
  //  Salon-Suche (debounced)
  //--------------------------------------

  // Debounced Salon-Suche: wartet 300ms nach der letzten Eingabe bevor die API
  // aufgerufen wird, um übermäßige Requests beim Tippen zu vermeiden.
  useEffect(() => {
    if (salonInputValue.length < 2) {
      setSalonOptions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSalonSearchLoading(true);
      try {
        const res = await api.getSalons(salonInputValue);
        setSalonOptions(
          res.data.map((s) => ({
            value: s.fiap_number,
            label: `${s.fiap_number} – ${s.name}`,
            data: s,
          }))
        );
      } catch (err) {
        console.error('Salon-Suche Fehler:', err);
        setSalonOptions([]);
      } finally {
        setSalonSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [salonInputValue]);

  //--------------------------------------
  //  Salon auswählen → Formular befüllen
  //--------------------------------------

  // Übernimmt Name, FIAP-Nummer und Typ aus dem gewählten Salon in das Eingabeformular.
  // PD (Photo Digital) wird auf 'digital', PP (Photo Print) auf 'print' gemappt.
  const handleSalonSelect = (selected) => {
    setSalonSelected(selected);
    if (!selected) return;

    const s = selected.data;
    updateForm('salonName',  s.name || '');
    updateForm('fiapNumber', s.fiap_number || '');

    // Typ aus FIAP-Liste übernehmen
    if (s.type) {
      const typeMap = { 'PD': 'digital', 'PP': 'print', 'digital': 'digital', 'print': 'print' };
      const mapped = typeMap[s.type.trim()] || 'digital';
      updateForm('competitionType', mapped);
    }
  };

  //--------------------------------------
  //  Bulk-Import (Matrix-Excel)
  //--------------------------------------

  // Lädt eine Matrix-Excel-Datei hoch und importiert alle markierten Ergebnisse.
  // Mit bulkReplaceMode=true werden vorher alle eigenen results und images gelöscht.
  const importBulk = useCallback(async (file) => {
    setBulkImporting(true);
    setBulkImportResult(null);

    const fd = new FormData();
    fd.append('file', file);

    try {
      const res = await api.bulkImport(fd, bulkReplaceMode);
      const d   = res.data;
      const modeLabel = d.replaced ? '🗑️ Daten geleert + ' : '';
      setBulkImportResult({
        success: true,
        message: `✅ ${modeLabel}${d.inserted} Ergebnisse importiert${d.errors > 0 ? `, ${d.errors} Fehler` : ''}`,
      });
      window.location.reload();
    } catch (err) {
      setBulkImportResult({
        success: false,
        message: `❌ Import fehlgeschlagen: ${err.response?.data?.error || err.message}`,
      });
    } finally {
      setBulkImporting(false);
    }
  }, [bulkReplaceMode]);

  //--------------------------------------
  //  Salon-Liste importieren
  //--------------------------------------

  // Lädt eine FIAP-Salon-Liste (xlsx oder pdf) hoch und importiert sie in die Datenbank.
  // Mit salonReplaceMode=true wird die Tabelle vor dem Import geleert (Neuimport).
  const importSalonList = useCallback(async (file) => {
    setSalonImporting(true);
    setSalonImportResult(null);

    const fd = new FormData();
    fd.append('file', file);

    try {
      const res = await axios.post(
        `/api/import-salons?replace=${salonReplaceMode}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const d = res.data;
      const modeLabel = d.replaced ? '🗑️ Tabelle geleert + ' : '';
      setSalonImportResult({
        success: true,
        message: `✅ ${modeLabel}${d.inserted} neu importiert, ${d.updated} aktualisiert${d.errors > 0 ? `, ${d.errors} Fehler` : ''}`,
      });
    } catch (err) {
      console.error('Salon Import Fehler:', err);
      setSalonImportResult({
        success: false,
        message: `❌ Import fehlgeschlagen: ${err.response?.data?.error || err.message}`,
      });
    } finally {
      setSalonImporting(false);
    }
  }, [salonReplaceMode]);

  //--------------------------------------
  //  Effect: ESC-Taste schließt Modal
  //--------------------------------------

  // Lädt das beste Bild je Subregion — neu bei refreshKey.
  useEffect(() => {
    axios.get('/api/best-images-per-region')
      .then(res => setBestPerRegion(res.data))
      .catch(err => console.error(err));
  }, [refreshKey]);

  // Lädt die Erfolgsquote je Subregion — neu bei refreshKey.
  useEffect(() => {
    axios.get('/api/region-performance')
      .then(res => setRegions(res.data))
      .catch(err => console.error(err));
  }, [refreshKey]);

  // Schließt das Bild-Vorschau-Modal bei Druck auf Escape.
  // Listener wird beim Unmount automatisch entfernt.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedImage(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  //--------------------------------------
  //  Effect: Statische Daten laden
  //--------------------------------------

  // Lädt alle Listen-Daten parallel beim ersten Rendern.
  // Fehlschlagen eines einzelnen Requests bricht den gesamten Ladevorgang ab.
  useEffect(() => {
    const loadStaticData = async () => {
      try {
        const [
          imagesRes,
          allImagesRes,
          countriesRes,
          overlimitRes,
          potentialRes,
          countryStatsRes,
          fiapCountriesRes,
          countryGapsRes,
          categoryStatsRes,
        ] = await Promise.all([
          api.getImages(),
          api.getAllImages(),
          api.getCountries(),
          api.getOverlimitImages(),
          api.getPotentialImages(),
          api.getAcceptancesByCountry(),
          api.getFiapCountries(),
          api.getCountryGaps(),
          api.getCategoryStats(),
        ]);

        setTopImages(imagesRes.data || []);
        setAllImages(allImagesRes.data || []);
        setCountries(countriesRes.data || []);
        setOverlimit(overlimitRes.data || []);
        setPotentialImages(potentialRes.data || []);
        setCountryStats(countryStatsRes.data || []);
        setFiapCountries(fiapCountriesRes.data || []);
        setCountryGaps(countryGapsRes.data?.missingCountries || []);
        setCategoryStats(categoryStatsRes.data || []);
      } catch (err) {
        console.error('Fehler beim Laden der Daten:', err);
      }
    };

    loadStaticData();
  }, [refreshKey]);

  //--------------------------------------
  //  Effect: Dashboard laden (Level-abhängig)
  //--------------------------------------

  // Wird bei jedem Wechsel des FIAP-Levels neu ausgeführt.
  // Wartet auf level !== null (wird erst nach dem Suggested-Level-Call gesetzt).
  useEffect(() => {
    if (!level) return;

    const loadDashboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await api.getDashboard(level);
        setData(res.data);
      } catch (err) {
        console.error('Dashboard Fehler:', err);
        setData(null);
        setError('Dashboard konnte nicht geladen werden');
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [level, refreshKey]);

  //--------------------------------------
  //  Flop Bilder
  //--------------------------------------

  // Lädt Bilder die mindestens 3× eingereicht aber nie angenommen wurden.
  useEffect(() => {
    const fetchData = async () => {
      try {
        const flopsRes = await api.getTopFlops();
        setTopFlops(flopsRes.data);
      } catch (err) {
        console.error("Fehler Top Flops:", err);
      }
    };
    fetchData();
  }, [refreshKey]);

  //--------------------------------------
  //  CSV Export
  //--------------------------------------

  // Exportiert alle Ergebnisse des Users als semikolon-getrennte CSV-Datei.
  // Das UTF-8-BOM am Anfang sorgt dafür, dass Excel die Datei korrekt mit Umlauten öffnet.
  const exportToCSV = useCallback(async () => {
    try {
      const res = await api.getExport();
      const exportData = res.data;

      if (!exportData?.length) {
        alert('Keine Exportdaten vorhanden.');
        return;
      }

      const csv = exportData
        .map((row) =>
          [
            row.acc,
            row.titleNr,
            `"${row.title}"`,
            `"${row.salon}"`,
            row.country,
            row.fiap,
            `"${row.award}"`,
          ].join(';')
        )
        .join('\n');

      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = 'fiap_export.csv';
      a.click();

      window.URL.revokeObjectURL(url);
      alert('Export erfolgreich erstellt!');
    } catch (err) {
      console.error('Export Fehler:', err);
      alert('Export fehlgeschlagen. Bitte Backend-Logs prüfen.');
    }
  }, []);

  //--------------------------------------
  //  Excel Upload (Results)
  //--------------------------------------

  // Lädt eine Excel-Datei hoch, die vom Python-Skript import_excel.py verarbeitet wird.
  // Mit excelReplaceMode=true werden eigene results und images vorher gelöscht.
  // Nach erfolgreichem Import wird die Seite neu geladen um die neuen Daten anzuzeigen.
  const uploadExcel = useCallback(async (file) => {
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      await api.uploadExcel(uploadFormData, excelReplaceMode);
      alert('Import erfolgreich!');
      window.location.reload();
    } catch (err) {
      console.error("UPLOAD ERROR:", err);
      alert(`Import fehlgeschlagen: ${err.response?.data?.error || err.message}`);
    }
  }, [excelReplaceMode]);

  //--------------------------------------
  //  Ergebnis speichern
  //--------------------------------------

  // Validiert und speichert ein einzelnes Wettbewerbsergebnis.
  // Priorisiert newTitle vor selectedTitle. Mappt den UI-Ergebnistyp (hm, medal, ...)
  // auf den award-String der DB; acceptance wird als null gespeichert.
  const submitResult = useCallback(async () => {
    // Leere Zeilen aus dem UI entfernen
    const validRows = imageRows.filter(r => r.newTitle.trim() || r.selectedTitle);
    if (validRows.length !== imageRows.length) {
      setImageRows(validRows.length > 0 ? validRows : [{ selectedTitle: '', newTitle: '' }]);
    }

    const titles = validRows.map(r => r.newTitle.trim() || r.selectedTitle);

    if (!titles.length || !formData.country || !formData.salonName || !formData.fiapNumber) {
      alert(lang.validationMsg);
      return;
    }

    let award = null;
    if (formData.resultType === 'acceptance') {
      award = null;
    } else if (formData.resultType === 'hm') {
      award = formData.awardText?.trim() || 'HM';
    } else {
      award = formData.resultType;
    }

    setSubmitting(true);

    try {
      for (const title of titles) {
        await api.postResult({
          title,
          country: formData.country,
          award,
          type: formData.competitionType,
          salonName: formData.salonName,
          fiapNumber: formData.fiapNumber,
        });
      }

      alert(titles.length > 1 ? `${titles.length} Bilder gespeichert!` : 'Gespeichert!');
      resetForm();
    } catch (err) {
      console.error('Speichern Fehler:', err);

      const response = err.response;
      const backendError = response?.data?.error;
      const backendDetail = response?.data?.detail;
      const statusCode = response?.status;

      let message = 'Speichern fehlgeschlagen';

      if (backendError) {
        message = `Fehler: ${backendError}`;
        if (backendDetail) message += `\nDetails: ${backendDetail}`;
      }

      message += `\nStatus: ${statusCode || '-'}`;
      alert(message);
    } finally {
      setSubmitting(false);
    }
  }, [formData, imageRows, lang]);

  //--------------------------------------
  //  Progress-Bar Renderer
  //--------------------------------------

  // Rendert eine beschriftete Fortschrittsleiste. value ist ein Dezimalwert (0–1),
  // wird intern auf Prozent umgerechnet und farblich per getProgressColor kodiert.
  const renderProgress = (label, value) => {
    if (value == null) return null;

    const percent = Math.min(value * 100, 100);
    const color = getProgressColor(value);

    return (
      <div className="progress-item">
        <p>
          {label}: {percent.toFixed(1)}%
        </p>
        <div className="progress-bar">
          <div
            className="progress"
            style={{
              width: `${percent}%`,
              backgroundColor: color,
            }}
          />
        </div>
      </div>
    );
  };

  //--------------------------------------
  //  Select-Optionen
  //--------------------------------------

  const countryOptions = (fiapCountries || []).map((c) => ({
    value: c,
    label: c,
  }));

  const countrySelectStyles = {
    container: (base) => ({
      ...base,
      width: '100%',
      maxWidth: '100%',
    }),
  };

  const salonSelectStyles = {
    container: (base) => ({
      ...base,
      width: '100%',
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '13px',
    }),
  };

  //--------------------------------------
  //  Sortierte Listen
  //--------------------------------------

  const nextSubmissions = [...potentialImages]
    .sort((a, b) => a.potential - b.potential)
    .slice(0, 7);

  const sortedPotentialImages = [...potentialImages].sort(
    (a, b) => a.potential - b.potential
  );

//--------------------------------------
//  Render
//--------------------------------------
return (
  <div className="app">

    {/*--------------------------------------*/}
    {/*  App-Header mit Logout               */}
    {/*--------------------------------------*/}
    <div className="app-header">
      <div className="app-header-left">
        <span
          className="app-header-user app-header-user--clickable"
          onClick={() => setShowPasswordDialog(true)}
          title="Passwort ändern"
        >
          {currentUser.role === 'admin' ? '👑' : '👤'} {currentUser.username} 🔑
        </span>
        {window.location.hostname.includes('devel') && (
          <button className="btn-org" onClick={toggleOrg} title="Organisation wechseln">
            {org === 'fiap' ? 'DVF' : 'FIAP'}
          </button>
        )}
        <button className="btn-lang" onClick={toggleLang} title="Sprache / Language">
          {lang === de ? 'EN' : 'DE'}
        </button>
        <button className="btn-darkmode" onClick={() => setDarkMode(d => !d)} title={darkMode ? lang.lightMode : lang.darkMode}>
          {darkMode ? '☀️' : '🌙'}
        </button>
      </div>
      <a href="https://miarto-solutions.de" target="_blank" rel="noreferrer">
        <img src="/logo_header.png" alt="Competa" className="header-logo" />
      </a>
      <div className="app-header-actions">
        <button className="btn-logout" onClick={() => setShowImageMgmt(true)}>
          {lang.btnImages}
        </button>
        <button className="btn-logout" onClick={() => setShowMaintenance(true)}>
          {lang.btnMaintenance}
        </button>
        <button className="btn-logout" onClick={onLogout}>{lang.logout}</button>
      </div>
    </div>

    {showUserDialog    && <UserManagementModal   onClose={() => setShowUserDialog(false)}    currentUser={currentUser} lang={lang} />}
    {showPasswordDialog && <ChangePasswordModal onClose={() => setShowPasswordDialog(false)} />}
    {showMaintenance   && <DataMaintenanceModal  onClose={() => setShowMaintenance(false)} onDataChanged={() => setRefreshKey(k => k + 1)} lang={lang} />}
    {showImageMgmt     && <ImageManagementModal  onClose={() => setShowImageMgmt(false)} onDataChanged={() => setRefreshKey(k => k + 1)} lang={lang} />}
    {showSessionLog    && <SessionLogModal       onClose={() => setShowSessionLog(false)} />}

    {/*--------------------------------------*/}
    {/*  Tab-Navigation                      */}
    {/*--------------------------------------*/}
    <div className="tab-nav">
      <button
        className={`tab-btn${activeTab === 'daten' ? ' tab-btn--active' : ''}`}
        onClick={() => setActiveTab('daten')}
      >
        📁 {lang.tabData}
      </button>
      <button
        className={`tab-btn${activeTab === 'dashboard' ? ' tab-btn--active' : ''}`}
        onClick={() => setActiveTab('dashboard')}
      >
        📊 {lang.tabDashboard}
      </button>
      {isAdmin && (
        <button
          className={`tab-btn${activeTab === 'admin' ? ' tab-btn--active' : ''}`}
          onClick={() => setActiveTab('admin')}
        >
          ⚙️ {lang.tabAdmin}
        </button>
      )}
    </div>

    {/*--------------------------------------*/}
    {/*  Tab: Daten & Wettbewerbe            */}
    {/*--------------------------------------*/}
    {activeTab === 'daten' && org === 'dvf' && (
      <DVFDataTab lang={lang} refreshKey={refreshKey} setRefreshKey={setRefreshKey} />
    )}

    {activeTab === 'daten' && org === 'fiap' && <>

    {/*--------------------------------------*/}
    {/*  Import Cards (oben, nebeneinander)  */}
    {/*--------------------------------------*/}
    <div className="import-grid">

      {/* Gesamtliste importieren */}
      <div className="card import-card">
        <div className="import-row">
          <div className="import-info">
            <div className="import-card-header">
              <h2>📥 {lang.importSubmissions}</h2>
              <a className="btn-template-sm" href="/templates/Template_Gesamtliste.xlsx"
                download onClick={() => setTimeout(() => setShowTemplateHint(true), 200)}>
                ⬇ {lang.templateDownload}
              </a>
            </div>
            <p className="import-hint">{lang.importSubmissionsHint}</p>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={excelReplaceMode}
                onChange={(e) => setExcelReplaceMode(e.target.checked)}
              />
              {lang.replaceSubmissions || 'Vorhandene Einreichungen ersetzen (Neuimport)'}
            </label>
          </div>
          <div className="import-action">
            <label className="file-upload-btn">
              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadExcel(file);
                  e.target.value = '';
                }}
              />
              <span>📂 {lang.selectFile}</span>
            </label>
          </div>
        </div>
      </div>

      {/* Bulk-Import (Matrix-Excel) */}
      <div className="card import-card">
        <div className="import-row">
          <div className="import-info">
            <div className="import-card-header">
              <h2>📊 {lang.importResults}</h2>
              <a className="btn-template-sm" href="/templates/Template_Bulk_Upload.xlsx"
                download onClick={() => setTimeout(() => setShowTemplateHint(true), 200)}>
                ⬇ {lang.templateDownload}
              </a>
            </div>
            <p className="import-hint">{lang.importResultsHint}</p>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={bulkReplaceMode}
                onChange={(e) => setBulkReplaceMode(e.target.checked)}
              />
              {lang.deleteOwnData}
            </label>
            {bulkImportResult && (
              <p className={`import-result-msg import-result-msg--${bulkImportResult.success ? 'success' : 'error'}`}>
                {bulkImportResult.message}
              </p>
            )}
          </div>
          <div className="import-action">
            <label className={`file-upload-btn${bulkImporting ? ' file-upload-btn--disabled' : ''}`}>
              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) importBulk(file);
                  e.target.value = '';
                }}
              />
              <span>{bulkImporting ? lang.importing : '📂 ' + lang.selectFile}</span>
            </label>
          </div>
        </div>
      </div>


    </div>

    {showTemplateHint && (
      <div className="dialog-overlay" onClick={() => setShowTemplateHint(false)}>
        <div className="dialog-box dialog-box--hint" onClick={e => e.stopPropagation()}>
          <div className="hint-icon">💡</div>
          <p className="hint-text">
            Vor dem Upload bitte die <span className="hint-red">rot markierten Texte</span> in der Vorlage entfernen und durch deine Daten ersetzen.
          </p>
          <button className="btn-primary" onClick={() => setShowTemplateHint(false)}>
            OK, verstanden
          </button>
        </div>
      </div>
    )}

    {/*--------------------------------------*/}
    {/*  Wettbewerb suchen                   */}
    {/*--------------------------------------*/}
    <SalonSearchCard lang={lang} />

    {/*--------------------------------------*/}
    {/*  Ergebnis hinzufügen                 */}
    {/*--------------------------------------*/}
    <div className="card entry-card import-card mb-24">
      <h2 className="entry-card-title">{lang.addResult}</h2>
      {/* Bild-Zeilen — eigener Container außerhalb des Grids */}
      <div className="image-rows-container">
        <div className="image-rows-labels">
          <span>{lang.selectImage}</span>
          <span>{lang.newImage}</span>
          <span className="image-rows-btn-placeholder" />
        </div>
        {imageRows.map((row, idx) => (
          <div key={idx} className="image-row">
            <select
              value={row.selectedTitle}
              onChange={(e) => updateImageRow(idx, 'selectedTitle', e.target.value)}
            >
              <option value="">{lang.existingImageOption}</option>
              {allImages.map((img, i) => (
                <option key={i} value={img.title}>{img.title}</option>
              ))}
            </select>
            <input
              type="text"
              value={row.newTitle}
              onChange={(e) => updateImageRow(idx, 'newTitle', e.target.value)}
              placeholder={lang.newImageTitle}
            />
            <button
              className="btn-add-row"
              onClick={addImageRow}
              title="Weiteres Bild hinzufügen"
              style={idx < imageRows.length - 1 ? {visibility:'hidden'} : {}}
            >+</button>
          </div>
        ))}
      </div>

      <div className="entry-form-grid">

        {/* ---- SALON AUS FIAP-LISTE WÄHLEN ---- */}
        <div className="form-field span-2">
          <label>{lang.selectFromFiap}</label>
          <Select
            placeholder={lang.fiapSearchPlaceholder}
            options={salonOptions}
            value={salonSelected}
            inputValue={salonInputValue}
            onInputChange={(val) => setSalonInputValue(val)}
            onChange={handleSalonSelect}
            classNamePrefix="rs"
            isLoading={salonSearchLoading}
            isClearable
            filterOption={() => true}  // Filterung erfolgt serverseitig
            noOptionsMessage={() =>
              salonInputValue.length < 2
                ? lang.fiapSearchMin
                : salonSearchLoading
                ? lang.fiapSearchLoading
                : lang.fiapSearchNone
            }
            styles={salonSelectStyles}
          />
          {salonSelected && (
            <p className="form-hint">{lang.salonSelected}</p>
          )}
        </div>

        {/* Salonname */}
        <div className="form-field">
          <label>{lang.salonNameLabel}</label>
          <input
            type="text"
            value={formData.salonName}
            onChange={(e) => updateForm('salonName', e.target.value)}
            placeholder="z. B. German International Photocup..."
          />
        </div>
        {/* FIAP Nummer */}
        <div className="form-field">
          <label>{lang.fiapNumberLabel}</label>
          <input
            type="text"
            value={formData.fiapNumber}
            onChange={(e) => updateForm('fiapNumber', e.target.value)}
            placeholder="z. B. 2025/063"
          />
        </div>
        {/* Land */}
        <div className="form-field">
          <label>{lang.country}</label>
          <Select
            options={countryOptions}
            value={countryOptions.find((c) => c.value === formData.country) || null}
            onChange={(selected) => updateForm('country', selected?.value || '')}
            classNamePrefix="rs"
            placeholder={lang.searchCountry}
            isClearable
            isSearchable
            styles={countrySelectStyles}
          />
        </div>
        {/* Salon-Typ */}
        <div className="form-field">
          <label>{lang.salonType}</label>
          <div className="radio-group compact-radio-group">
            {COMPETITION_TYPES.map((type) => (
              <label key={type.value} className="radio-item">
                <input
                  type="radio"
                  value={type.value}
                  checked={formData.competitionType === type.value}
                  onChange={(e) => updateForm('competitionType', e.target.value)}
                />
                {type.label}
              </label>
            ))}
          </div>
        </div>
      </div>
      {/* Ergebnis-Typ */}
      <p className="section-label">{lang.resultLabel}</p>
      <div className="radio-group result-radio-group">
        {RESULT_TYPES.map((type) => (
          <label key={type.value} className="radio-item">
            <input
              type="radio"
              value={type.value}
              checked={formData.resultType === type.value}
              onChange={(e) => updateForm('resultType', e.target.value)}
            />
            {type.label}
          </label>
        ))}
      </div>
      {/* HM Zusatzfeld */}
      {formData.resultType === 'hm' && (
        <div className="hm-input-wrap">
          <input
            type="text"
            value={formData.awardText}
            onChange={(e) => updateForm('awardText', e.target.value)}
            placeholder="Optional: z. B. NFFF HM"
            className="hm-input"
          />
          <p className="hm-hint">Leer = HM</p>
        </div>
      )}
      {/* Action Buttons */}
      <div className="button-row">
        <button className="btn-primary" onClick={submitResult} disabled={submitting}>
          {submitting ? lang.saving : lang.save}
        </button>
        <button className="btn-secondary" onClick={exportToCSV}>
          Export CSV
        </button>
      </div>
    </div>


    </>}

    {/*--------------------------------------*/}
    {/*  Tab: Dashboard                       */}
    {/*--------------------------------------*/}
    {activeTab === 'dashboard' && org === 'dvf' && (
      <DVFDashboard lang={lang} />
    )}

    {activeTab === 'dashboard' && org === 'fiap' && <>


      <div className="top-bar">
        <label htmlFor="level-select">{lang.distinction} </label>
        <select
          id="level-select"
          value={level || ''}
          onChange={(e) => setLevel(e.target.value)}
        >
          {LEVELS.map((l) => (
            <option key={l.value} value={l.value}>
              {l.label}
            </option>
          ))}
        </select>
      </div>

      {/* Loading / Error */}
      {loading && <p className="loading">Lade Dashboard...</p>}
      {error && <p className="error">{error}</p>}

      {/*--------------------------------------*/}
      {/*  Statistiken                         */}
      {/*--------------------------------------*/}

      {data && !loading && (
        <div className="grid">

        <div className="card">
          <h2>📊 {lang.overview}</h2>

          <ul className="flop-list">
            {[
              { label: lang.acceptances, value: data.stats.cappedAcceptances },
              { label: lang.images, value: data.stats.images },
              { label: lang.countries, value: data.stats.countries },
              { label: lang.discarded, value: data.stats.overflow }
            ].map((item, i) => (
              <li key={i} className="flop-item">

                <div className="flop-title">
                  {item.label}
                </div>

                <div className="flop-meta">
                  <span className="flop-category"></span>

                  <span className="badge gray">
                    {item.value}
                  </span>
                </div>

              </li>
            ))}
          </ul>
        </div>
        
        <div className="card">
          <h2>📌 {lang.required}</h2>

          <ul className="flop-list">

            <li className="flop-item">
              <div className="flop-title">{lang.acceptances}</div>

              <div className="flop-meta">
                <span className="flop-category"></span>
                <span className="badge gray">
                  {data.required.acceptances}
                </span>
              </div>
            </li>

            <li className="flop-item">
              <div className="flop-title">{lang.images}</div>

              <div className="flop-meta">
                <span className="flop-category"></span>
                <span className="badge gray">
                  {data.required.images}
                </span>
              </div>
            </li>

            <li className="flop-item">
              <div className="flop-title">{lang.countries}</div>

              <div className="flop-meta">
                <span className="flop-category"></span>
                <span className="badge gray">
                  {data.required.countries}
                </span>
              </div>
            </li>

          </ul>
        </div>

          <div className="card">
            <h2>📈 {lang.progress}</h2>
            {renderProgress(lang.acceptances, data.progress.acceptances)}
            {renderProgress(lang.images, data.progress.images)}
            {renderProgress(lang.countries, data.progress.countries)}
          </div>

        </div>
      )}

      {/*--------------------------------------*/}
      {/*  Bilder-Listen                       */}
      {/*--------------------------------------*/}

      <div className="grid">

      <div className="card">
          <h2>⭐ {lang.topImages}</h2>

          <ul className="flop-list">
            {topImages.map((img, i) => (
              <li key={i} className="flop-item">

                {/* 🔹 Titel */}
                <div
                  className={`flop-title${img.thumbnail_path ? ' clickable' : ''}`}
                  onClick={() => img.thumbnail_path && setSelectedImage({ path: img.thumbnail_path, title: img.title, category: null, acceptances: img.capped_acceptances })}
                >
                  {img.title}
                </div>

                {/* 🔹 Meta darunter */}
                <div className="flop-meta">
                  <span className="flop-category">

                  </span>

                  <span className={`badge ${
                    img.capped_acceptances >= 8
                      ? "success"
                      : img.capped_acceptances >= 5
                      ? "warning"
                      : "danger"
                  }`}>
                    {img.capped_acceptances}
                  </span>
                </div>

              </li>
            ))}
          </ul>
        </div>

        <div className="card">
          <h2>🚫 {lang.overlimit}</h2>

          <ul className="flop-list">
            {overlimit.map((img, i) => (
              <li key={i} className="flop-item">

                {/* 🔹 LINKS (Titel + Subline) */}
                <div className="flop-left">
                  <div className="flop-title">
                    {img.title}
                  </div>

                  <div className="flop-category">
                    {lang.acceptances}: {img.raw_acceptances}
                  </div>
                </div>

                {/* 🔹 RECHTS (Badge) */}
                <span className="badge danger">
                  +{img.overflow}
                </span>

              </li>
            ))}
          </ul>
        </div>

        <div className="card">
          <h2>🚀 {lang.schedule}</h2>

          <ul className="flop-list">
            {nextSubmissions.map((img, i) => {
              const remaining = Math.max(0, 10 - img.acceptances);

              return (
                <li key={i} className="flop-item">

                  {/* 🔹 Titel */}
                  <div className={`flop-title${img.thumbnail_path ? ' clickable' : ''}`}
                      onClick={() => img.thumbnail_path && setSelectedImage({ path: img.thumbnail_path, title: img.title, category: img.category, acceptances: Math.max(0, 10 - (img.acceptances || 0)) })}>
                    {img.title}
                  </div>

                  {/* 🔹 Meta darunter */}
                  <div className="flop-meta">
                    <span className="flop-category">
                    
                    </span>

                    <span className={`badge ${
                      remaining <= 2
                        ? "danger"
                        : remaining <= 5
                        ? "warning"
                        : "success"
                    }`}>
                      {remaining}
                    </span>
                  </div>

                </li>
              );
            })}
          </ul>
        </div>

      </div>

      {/*--------------------------------------*/}
      {/*  Erweiterte Listen                   */}
      {/*--------------------------------------*/}

      <div className="grid">

      <div className="card">
          <h2>🎯 {lang.potentialImages}</h2>

          <ul className="flop-list">
            {potentialImages.map((p, i) => (
              <li key={i} className="flop-item">

                <div className="flop-left">
                  <div className={`flop-title${p.thumbnail_path ? ' clickable' : ''}`} onClick={() => p.thumbnail_path && setSelectedImage({ path: p.thumbnail_path, title: p.title, category: p.category, acceptances: p.acceptances })}>{p.title}</div>
                  <div className="flop-category">{p.category}</div>
                </div>

                <span
                  className={`badge ${
                    p.acceptances >= 8
                      ? "danger"
                      : p.acceptances >= 5
                      ? "warning"
                      : "neutral"
                  }`}
                >
                  {p.acceptances}
                </span>

              </li>
            ))}
          </ul>
        </div>

                <div className="card">
          <h2>💀 {lang.topFlops}</h2>

          {topFlops.length === 0 ? (
            <p>{lang.noFlops}</p>
          ) : (
            <ul className="flop-list">
              {topFlops.map((f, i) => (
                <li key={i} className="flop-item">
                  
                  <div className="flop-left">
                    <div className={`flop-title${f.thumbnail_path ? ' clickable' : ''}`} onClick={() => f.thumbnail_path && setSelectedImage({ path: f.thumbnail_path, title: f.title, category: f.category, acceptances: null })}>{f.title}</div>
                    <div className="flop-category">{f.category}</div>
                  </div>

                    <span
                    className={`badge ${
                      f.submissions >= 10
                        ? "danger"
                        : f.submissions >= 6
                        ? "warning"
                        : "neutral"
                    }`}
                  >
                      {f.submissions}
                    </span>

                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card">
          <h2>📊 {lang.successByCategory}</h2>

          <ul className="category-list">
            {categoryStats.map((cat, i) => (
              <li key={i} className="category-item">
                <span className="category-name">{cat.category}</span>

                <span
                  className={`badge ${
                    cat.total_results > 20
                      ? "success"
                      : cat.total_results >= 10
                      ? "warning"
                      : "neutral"
                  }`}
                >
                  {cat.total_results}
                </span>

              </li>
            ))}
          </ul>
        </div>

        <div className="card">
        <h2>🌍 {lang.countriesSuccess}</h2>
          <ul className="category-list">
            {countries.map((c, i) => (
              <li key={i} className="category-item">

                <span className="category-name">{c.country}</span>

                <span
                  className={`badge ${
                    c.acceptances > 20
                      ? "success"
                      : c.acceptances >= 10
                      ? "warning"
                      : "neutral"
                  }`}
                >
                  {c.acceptances}
                </span>

              </li>
            ))}
          </ul>
        </div>

        <div className="card">
          <h2>🌍 {lang.regionsSuccess}</h2>

          <ul className="category-list">
          {[...regions]
            .sort((a, b) => b.success_rate - a.success_rate)
            .map((r, i) => (
              <li key={i} className="category-item region-item">

                <div className="category-left">
                  <div className="category-name">{r.region}</div>

                  {r.success_rate > 0 && (
                    <div className="category-sub">
                      {r.countries?.slice(0, 4).join(', ')}
                      {r.countries?.length > 4 && ' …'}
                    </div>
                  )}
                </div>

                <span
                className={`badge ${
                  r.success_rate < 10
                    ? "danger"
                    : r.success_rate < 20
                    ? "warning"
                    : "success"
                }`}
              >
                <div className="badge-main">{r.success_rate}%</div>
                <div className="badge-sub">({r.acceptances}/{r.submissions})</div>
              </span>

              </li>
            ))}
          </ul>
        </div>

        <div className="card">
          <h2>🏆 {lang.bestPerRegion}</h2>

          {bestPerRegion.length === 0 ? (
            <p>{lang.noData}</p>
          ) : (
            <ul className="category-list">
              {bestPerRegion.map((r, i) => (
                <li key={i} className="category-item">

                  <div>
                    <div className="category-name">{r.region}</div>
                    <div className="category-sub">{r.title}</div>
                  </div>

                  <span className="category-count">
                    {r.acceptances}
                  </span>

                </li>
              ))}
            </ul>
          )}
        </div>
        
      </div>

    </>}

    {/*--------------------------------------*/}
    {/*  Tab: Administration                 */}
    {/*--------------------------------------*/}
    {activeTab === 'admin' && isAdmin && (
      <AdminTab currentUser={currentUser} lang={lang} />
    )}

    {/*--------------------------------------*/}
    {/*  Bild-Modal (immer sichtbar)         */}
    {/*--------------------------------------*/}

    {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedImage(null)} aria-label="Schließen">×</button>
            <img src={`/images/${selectedImage.path}`} alt={selectedImage.title} />
            <div className="modal-info">
              <span className="modal-title">{selectedImage.title}</span>
              <div className="modal-meta">
                {selectedImage.category && (
                  <span className="modal-badge modal-badge--cat">{selectedImage.category}</span>
                )}
                {selectedImage.acceptances != null && (
                  <span className="modal-badge modal-badge--acc">
                    {selectedImage.acceptances} ×
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/*--------------------------------------*/}
      {/*  Footer                              */}
      {/*--------------------------------------*/}

      <footer className="footer">© 2026 powered by <a href="https://miarto-solutions.de" target="_blank" rel="noreferrer" className="footer-link">miarto-solutions</a></footer>

    </div>
  );
}

export default App;
